import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: FormGroup;
  loading = false;
  error = '';
  successMessage = '';
  registeredEmail = '';
  resendCooldown = 0;
  resendLoading = false;
  showConfirmModal = false;
  private cooldownInterval?: ReturnType<typeof setInterval>;
  private subscription = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      acceptTerms: [false, [Validators.requiredTrue]],
      marketingConsent: [false]
    });
  }

  ngOnInit(): void {
    this.subscription.add(
      this.authService.isAuthenticated$.subscribe(isAuthenticated => {
        if (isAuthenticated) {
          this.router.navigate(['/dashboard']);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }

  onSubmit(): void {
    if (this.registerForm.invalid) {
      return;
    }
    // Open confirmation modal instead of submitting directly
    this.error = '';
    this.showConfirmModal = true;
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }

  async confirmRegister(): Promise<void> {
    if (this.registerForm.invalid || this.loading) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.successMessage = '';

    const { name, email, acceptTerms, marketingConsent } = this.registerForm.value;

    try {
      const response = await this.authService.register(email, name, acceptTerms, marketingConsent);

      if (response.success) {
        this.registeredEmail = email;
        this.showConfirmModal = false;
        this.successMessage = 'Te hemos enviado un email para activar tu cuenta. Revisa tu bandeja de entrada y haz clic en el enlace de activacion. El enlace expira en 48 horas.';
        this.startCooldown(60);
      } else {
        this.error = response.message || 'Error al crear la cuenta';
        this.showConfirmModal = false;
      }
    } catch (error: any) {
      this.error = error.message || 'Error al registrar usuario';
      this.showConfirmModal = false;
    } finally {
      this.loading = false;
    }
  }

  async resendActivation(): Promise<void> {
    if (this.resendCooldown > 0 || this.resendLoading || !this.registeredEmail) return;

    this.resendLoading = true;
    this.error = '';

    try {
      const response = await this.authService.resendActivationEmail(this.registeredEmail);
      if (response.success) {
        this.startCooldown(60);
      } else {
        this.error = response.message || 'Error al reenviar el email';
      }
    } catch (error: any) {
      this.error = error.message || 'Error al reenviar el email';
    } finally {
      this.resendLoading = false;
    }
  }

  private startCooldown(seconds: number): void {
    this.resendCooldown = seconds;
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
    this.cooldownInterval = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        clearInterval(this.cooldownInterval);
      }
    }, 1000);
  }

  get f() {
    return this.registerForm.controls;
  }
}
